import { useId } from 'react';
import { Box, ButtonBase, Stack, Typography, useTheme } from '@mui/material';
import { useChatBot, useGetConfig } from './ChatBot';
import { trackWidgetEvent } from '../utils/analytics';
import { scaledFontSize } from './createWidgetTheme';
import { useWidgetBrandColors } from './useWidgetBrandColors';
import { useEffectiveQuickStartPlacement } from '../hooks/useEffectiveQuickStartPlacement';
import { getLauncherTeaserMode } from '../utils/getLauncherTeaserMode';
import { useWidgetI18n } from '../i18n/WidgetI18nProvider';
import { visuallyHiddenSx } from './visuallyHiddenSx';

export function QuickStartChips() {
  const theme = useTheme();
  const { t, locale } = useWidgetI18n();
  const groupLabelId = useId();
  const { primaryOnPaper } = useWidgetBrandColors();
  const config = useGetConfig();
  const { history, send, isVoiceActive, hasSentMessageInSession } = useChatBot();
  const placement = useEffectiveQuickStartPlacement();

  const buttons = config.quickStartButtons ?? [];
  const hasUserInitiated = history.some((msg) => msg.role === 'user');
  const hideForFrequency =
    config.quickStartFrequency === 'until_first_message' &&
    Boolean(hasSentMessageInSession);

  // When the institution selected "on_hover" placement (and we're not on a
  // mobile viewport, where placement is overridden to within_chat), the chips
  // are rendered above the FAB launcher instead of inside the chat thread.
  if (placement === 'on_hover') return null;
  if (buttons.length === 0 || hasUserInitiated || isVoiceActive || hideForFrequency)
    return null;

  const handleChipClick = (button: { label: string; message: string }) => {
    trackWidgetEvent('Chat Widget - Quick Start Chip Clicked', {
      buttonLabel: button.label,
      teaserMode: getLauncherTeaserMode(config),
    });
    send(button.message, { isQuickStartButton: true });
  };

  return (
    // Match the agent message blurb layout (see WelcomeMessage / Message): a
    // left-aligned outer box with px:2 gutter and an inner box capped at 80%
    // so chips line up at the same width as the bot bubble above them.
    <Box
      display="flex"
      justifyContent="flex-start"
      sx={{ width: '100%', px: 2, mt: 1.5, mb: 1 }}
    >
      <Box sx={{ maxWidth: '80%', width: '100%' }}>
        <Typography
          component="span"
          id={groupLabelId}
          lang={locale}
          sx={visuallyHiddenSx}
        >
          {t('embed.quickStartOptions')}
        </Typography>
        <Stack role="group" aria-labelledby={groupLabelId} spacing={1}>
          {buttons.map((button, index) => (
            <ButtonBase
              key={`${button.label}-${index}`}
              aria-label={button.label}
              onClick={() => handleChipClick(button)}
              sx={{
                minHeight: 46,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'center',
                textAlign: 'left',
                px: 1.5,
                py: 1,
                borderRadius: 3,
                border: '1px solid',
                borderColor: primaryOnPaper,
                bgcolor: 'background.paper',
                transition: 'box-shadow 150ms ease',
                '&:hover, &:focus-visible': {
                  boxShadow: theme.shadows[2],
                },
              }}
            >
              <Typography
                fontSize={scaledFontSize(theme, 13)}
                fontWeight={500}
                sx={{
                  color: primaryOnPaper,
                  lineHeight: 1.3,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  width: '100%',
                }}
              >
                {button.label}
              </Typography>
              <Typography
                fontSize={scaledFontSize(theme, 11)}
                fontWeight={400}
                color="textSecondary"
                sx={{
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {button.message}
              </Typography>
            </ButtonBase>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}
